import { type WorkspacePlan } from '@up2stay/up2promo-api-client';
import { Badge } from '@up2stay/up2promo-ui';

import { useEntitlementLabels } from '@/core/components/billing/upgrade-surfaces';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';

type BillingGrantSummary = {
  plan: string;
  memberLimit: number | null;
  reputationLocationLimit: number | null;
  reason: string;
  grantedAt: string;
  expiresAt: string | null;
};

type BillingGrantStatusCardProps = {
  billingGrant: BillingGrantSummary;
};

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

export function BillingGrantStatusCard({
  billingGrant,
}: BillingGrantStatusCardProps) {
  const dict = useDictionary();
  const locale = useLocale();
  const grantDict = dict.workspace.billing.grantStatus;
  const { getPlanLabel } = useEntitlementLabels();

  return (
    <SettingsSection
      id="billing-grant"
      title={grantDict.title}
      footer={grantDict.description}
      grouped
    >
      <div className="flex flex-col gap-3 px-4 py-3.5 sm:px-5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-2xl font-semibold tracking-tight text-foreground">
            {getPlanLabel(billingGrant.plan as WorkspacePlan)}
          </p>
          <Badge variant="secondary">{grantDict.managedBadge}</Badge>
        </div>

        <div className="flex flex-col gap-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
          <p>
            {grantDict.reason.replace(
              '{reason}',
              grantDict.reasons[
                billingGrant.reason as keyof typeof grantDict.reasons
              ] ?? billingGrant.reason,
            )}
          </p>
          {billingGrant.memberLimit != null ? (
            <p>
              {grantDict.memberLimit.replace(
                '{count}',
                String(billingGrant.memberLimit),
              )}
            </p>
          ) : null}
          {billingGrant.reputationLocationLimit != null ? (
            <p>
              {grantDict.reputationLimit.replace(
                '{count}',
                String(billingGrant.reputationLocationLimit),
              )}
            </p>
          ) : null}
          {billingGrant.expiresAt ? (
            <p>
              {grantDict.expiresAt.replace(
                '{date}',
                formatDate(billingGrant.expiresAt, locale),
              )}
            </p>
          ) : null}
        </div>
      </div>
    </SettingsSection>
  );
}
